import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import YhInput from "../../../../../ui/YhInput";
import YhSelect from "../../../../../ui/YhSelect";
import YhButton from "../../../../../ui/YhButton";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../helpers/methods";
import notifiers from "../../../../../global/constants/NotificationConstants";
import customErrorHandler from "../../../../../utils/customErrorHandler";
import { getRoleById, addRole, updateRole } from "./roleService";
import { getAllHierarchyMeta } from "../../../../MetaData/components/MetaList/components/CompanyHierarchyList/companyHierarchyService";
import urls from "../../../../../global/constants/UrlConstants";

const PRIVILEGES = [
  "view",
  "create",
  "edit",
  "delete",
  "download",
  "export",
  "import",
  "see all",
  "full control",
] as const;

const PRIVILEGE_COLS = [
  { key: "view", label: "View", icon: "eye" },
  { key: "create", label: "Create", icon: "plus" },
  { key: "edit", label: "Edit", icon: "pencil" },
  { key: "delete", label: "Delete", icon: "trash" },
  { key: "download", label: "Download", icon: "download" },
  { key: "export", label: "Export", icon: "export" },
  { key: "import", label: "Import", icon: "import" },
  { key: "see all", label: "See All", icon: "users" },
  { key: "full control", label: "Full Control", icon: "shield" },
] as const;

const DEFAULT_INFO: Record<string, string[]> = {
  Dashboard: [],
  ADMIN: [],
  HR: [],
  "MY-Dashboard": [],
  Setting: [],
  "Meta Data": [],
  Account: [],
  Organizations: [],
  Integrations: [],
  AuditLogs: [],
  Automation: [],
  "Roles & Privileges": [],
  "System Settings": [],
  Profile: [],
  Security: [],
  "CRM Preferences": [],
  Project: [],
  "Project List": [],
  Team: [],
  "Team List": [],
  "All Leaves": [],
  Meeting: [],
  "Meeting List": [],
  Booking: [],
  Leave: [],
  Attendance: [],
  "All Attendance": [],
  Salary: [],
  Leads: [],
  "Lead Form": [],
  Feedback: [],
  Registrations: [],
  Payment: [],
  Marketing: [],
  "WhatsApp Marketing": [],
  "Channel Partner": [],
  "Channel Partners": [],
  "CP Bulk Upload": [],
  "Add CP Lead": [],
  "All Lead": [],
  "Hometatva Leads": [],
  "My Leads": [],
  "Team Leads": [],
  "Service Leads": [],
  "Subscribe Leads": [],
  "Lead Transfer": [],
  "Archive Lead List": [],
  "Bulk Upload Project": [],
  "Bulk Upload Master": [],
  "Leads Bulk Upload": [],
  "Call Datasets": [],
  "Upload Call Data": [],
  Customer: [],
  User: [],
  "User List": [],
  Report: [],
  "Pre Sales Report": [],
  "Sales Report": [],
  "Source Report": [],
  "Project Report": [],
  "Closure Report": [],
  "Call Logs": [],
  "Calling Report": [],
  "Daily Calling Report": [],
};

/** Role.info is stored as a JSON string in Mongo; API may return string or object. */
const parseRoleInfoPayload = (raw: unknown): Record<string, unknown> => {
  let value: unknown = raw;
  for (let i = 0; i < 2; i += 1) {
    if (typeof value !== "string") break;
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
};

const toPrivilegeList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).filter(Boolean);
  }
  return [];
};

/** Always return a fresh map of module → privilege string[]. */
const normalizeRoleInfo = (raw?: unknown): Record<string, string[]> => {
  const parsed = parseRoleInfoPayload(raw);
  const next: Record<string, string[]> = {};
  Object.keys(DEFAULT_INFO).forEach((key) => {
    next[key] = toPrivilegeList(parsed[key]);
  });
  Object.keys(parsed).forEach((key) => {
    if (!(key in next)) {
      next[key] = toPrivilegeList(parsed[key]);
    }
  });
  return next;
};

const cloneInfo = (info: Record<string, string[]>): Record<string, string[]> => {
  const next: Record<string, string[]> = {};
  Object.keys(info || {}).forEach((key) => {
    next[key] = toPrivilegeList(info[key]);
  });
  return next;
};

/** Friendly labels in the matrix (keys stay as privilege keys). */
const MODULE_LABELS: Record<string, string> = {
  Setting: "Setting",
  Account: "Account",
  "Meta Data": "Meta Data",
  Organizations: "Organizations",
  Integrations: "Integrations",
  AuditLogs: "Audit Logs",
  Automation: "Automation Lead Flow",
  "Roles & Privileges": "Roles & Privileges",
  "System Settings": "System Settings",
  Profile: "Profile",
  Security: "Security",
  "CRM Preferences": "CRM Preferences",
  Marketing: "Marketing",
  "WhatsApp Marketing": "WhatsApp Marketing",
  Registrations: "Registrations",
  Payment: "Payment",
  "Channel Partner": "Partners",
  "Channel Partners": "Partners",
  "CP Bulk Upload": "Bulk Upload",
  "Add CP Lead": "Add Partner Lead",
  "Leads Bulk Upload": "Leads Bulk Upload",
  "Call Datasets": "Call Datasets",
  "Upload Call Data": "Upload Call Data",
};

const moduleLabel = (key: string) => MODULE_LABELS[key] || key;

const safeDomId = (value: string) =>
  String(value).replace(/[^a-zA-Z0-9_-]/g, "_");

const MODULE_TREE: { parent: string; children: string[] }[] = [
  { parent: "Dashboard", children: ["ADMIN", "HR", "MY-Dashboard"] },
  {
    parent: "Setting",
    children: [
      "Account",
      "Meta Data",
      "Organizations",
      "Integrations",
      "AuditLogs",
      "Automation",
      "Roles & Privileges",
      "System Settings",
      "Profile",
      "Security",
      "CRM Preferences",
    ],
  },
  { parent: "Project", children: ["Project List"] },
  {
    parent: "Team",
    children: [
      "Team List",
      "All Leaves",
      "Leave",
      "Attendance",
      "All Attendance",
      "Salary",
    ],
  },
  { parent: "Meeting", children: ["Meeting List", "Booking"] },
  { parent: "Marketing", children: ["WhatsApp Marketing"] },
  {
    parent: "Channel Partner",
    children: ["Channel Partners", "CP Bulk Upload", "Add CP Lead"],
  },
  {
    parent: "Leads",
    children: [
      "Lead Form",
      "Feedback",
      "Registrations",
      "Payment",
      "All Lead",
      "Hometatva Leads",
      "My Leads",
      "Team Leads",
      "Service Leads",
      "Subscribe Leads",
      "Lead Transfer",
      "Archive Lead List",
      "Bulk Upload Project",
      "Bulk Upload Master",
      "Leads Bulk Upload",
      "Call Datasets",
      "Upload Call Data",
    ],
  },
  { parent: "Customer", children: ["User List"] },
  {
    parent: "Report",
    children: [
      "Pre Sales Report",
      "Sales Report",
      "Source Report",
      "Project Report",
      "Closure Report",
      "Call Logs",
      "Daily Calling Report",
    ],
  },
];

const emptyAuthManager = () => ({
  role_name: "",
  description: "",
  isOrganization: 0,
  hierarchyId: "",
  info: normalizeRoleInfo(),
});

const ColIcon = ({ name }: { name: string }) => {
  const c = {
    width: 14,
    height: 14,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
  } as const;
  switch (name) {
    case "eye":
      return (
        <svg {...c}>
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "plus":
      return (
        <svg {...c}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "pencil":
      return (
        <svg {...c}>
          <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      );
    case "trash":
      return (
        <svg {...c} stroke="#EF4444">
          <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
        </svg>
      );
    case "download":
      return (
        <svg {...c}>
          <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
        </svg>
      );
    case "export":
      return (
        <svg {...c}>
          <path d="M12 21V9M7 14l5-5 5 5M5 3h14" />
        </svg>
      );
    case "import":
      return (
        <svg {...c}>
          <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
        </svg>
      );
    case "shield":
      return (
        <svg {...c}>
          <path d="M12 3l8 4v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V7l8-4z" />
        </svg>
      );
    case "users":
      return (
        <svg {...c}>
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
      );
    default:
      return null;
  }
};

const PrivilegeCheck = ({
  checked,
  indeterminate,
  onToggle,
  inputId,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onToggle: (checked: boolean) => void;
  /** Unique per cell — duplicate name= across rows crashes React 19 reconciliation. */
  inputId: string;
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useLayoutEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = !!indeterminate && !checked;
    }
  }, [indeterminate, checked]);

  return (
    <label
      className="inline-flex cursor-pointer items-center justify-center"
      htmlFor={inputId}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          onToggle(e.target.checked);
        }}
      />
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-[5px] border transition ${
          checked || indeterminate
            ? "border-[#A02448] bg-[#A02448] text-white"
            : "border-[#CBD5E1] bg-white"
        }`}
      >
        {checked ? (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        ) : indeterminate ? (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
          >
            <path d="M5 12h14" />
          </svg>
        ) : null}
      </span>
    </label>
  );
};

class RolePageErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="m-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-semibold">Roles & Privileges failed to render</p>
          <p className="mt-1 break-words">{this.state.error.message}</p>
          <button
            type="button"
            className="mt-3 rounded-lg bg-red-600 px-3 py-1.5 text-white"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
};

interface CustomProps {
  isDialogOpen?: boolean;
  handleCloseAddTeamMetaData?: Function;
  idd?: string;
  setIdd?: Function;
}

const AddRolePage = (props: CustomProps = {}) => {
  const history = useHistory();
  const { id: routeId } = useParams<{ id?: string }>();
  const isEmbedded = props.isDialogOpen !== undefined;
  const editId = props.idd || routeId || "";

  const [rows, setRows] = useState<any>({ authManager: emptyAuthManager() });
  const [hierarchyData, setHierarchyData] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(MODULE_TREE.map((m) => [m.parent, true]))
  );
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    if (props.handleCloseAddTeamMetaData) {
      props.handleCloseAddTeamMetaData(false);
      props.setIdd?.("");
      return;
    }
    history.push(urls.rolesPrivilegesViewPath);
  };

  useEffect(() => {
    if (isEmbedded && !props.isDialogOpen) {
      props.setIdd?.("");
      setRows({ authManager: emptyAuthManager() });
      return;
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDialogOpen, editId]);

  useEffect(() => {
    const fetchHierarchyData = async () => {
      const hierarchyResponse = await getAllHierarchyMeta();
      const orgHierarchyData =
        rows.authManager.isOrganization === 1
          ? (hierarchyResponse.data || []).filter(
              (item: any) => item.isOrganization === 1
            )
          : (hierarchyResponse.data || []).filter(
              (item: any) => item.isOrganization === 0
            );

      setHierarchyData(
        orgHierarchyData.map((i: any) => ({
          id: i._id,
          label: i.hierarchyName,
        }))
      );
    };
    fetchHierarchyData();
  }, [rows.authManager.isOrganization]);

  const init = async () => {
    try {
      if (editId) {
        const response: any = await getRoleById(editId);
        customErrorHandler(response);
        setRows({
          authManager: {
            role_name: response.data?.role_name || "",
            description: response.data?.description || "",
            isOrganization: response.data.isOrganization || 0,
            hierarchyId: response.data?.hierarchyId || "",
            info: normalizeRoleInfo(response.data?.info),
          },
        });
      } else {
        setRows({ authManager: emptyAuthManager() });
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleOnChangeInputField = (event: React.ChangeEvent<any>) => {
    setRows((prevState: any) => ({
      authManager: {
        ...prevState.authManager,
        [event.target.name]: event.target.value,
      },
    }));
  };

  const toggleModulePrivilege = (
    moduleName: string,
    privilege: string,
    checked: boolean
  ) => {
    setRows((prevState: any) => {
      const info = cloneInfo(prevState.authManager?.info || {});
      const current = toPrivilegeList(info[moduleName]);

      if (privilege === "full control") {
        info[moduleName] = checked ? [...PRIVILEGES] : [];
      } else {
        const index = current.indexOf(privilege);
        if (checked && index === -1) current.push(privilege);
        if (!checked && index !== -1) current.splice(index, 1);
        if (!checked) {
          const fc = current.indexOf("full control");
          if (fc !== -1) current.splice(fc, 1);
        }
        info[moduleName] = current;
      }

      return {
        authManager: {
          ...prevState.authManager,
          info,
        },
      };
    });
  };

  const toggleParentPrivilege = (
    parent: string,
    children: string[],
    privilege: string,
    checked: boolean
  ) => {
    setRows((prev: any) => {
      const nextInfo = cloneInfo(prev.authManager?.info || {});
      [parent, ...children].forEach((mod) => {
        if (privilege === "full control") {
          nextInfo[mod] = checked ? [...PRIVILEGES] : [];
        } else {
          const cur = toPrivilegeList(nextInfo[mod]);
          const idx = cur.indexOf(privilege);
          if (checked && idx === -1) cur.push(privilege);
          if (!checked && idx !== -1) {
            cur.splice(idx, 1);
            const fc = cur.indexOf("full control");
            if (fc !== -1) cur.splice(fc, 1);
          }
          nextInfo[mod] = cur;
        }
      });
      return {
        authManager: {
          ...prev.authManager,
          info: nextInfo,
        },
      };
    });
  };

  const submitForm = async () => {
    try {
      setSaving(true);
      if (editId) {
        const response: any = await updateRole({
          ...rows.authManager,
          id: editId,
        });
        customErrorHandler(response);
        openSuccessNotification(response?.message);
      } else {
        const response: any = await addRole({
          ...rows.authManager,
          isOrganization: rows.authManager.isOrganization || 0,
        });
        customErrorHandler(response);
        openSuccessNotification(response?.message);
      }
      handleClose();
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  const hierarchyOptions = useMemo(
    () =>
      hierarchyData.map((h: any) => ({
        label: h.label,
        value: h.id,
      })),
    [hierarchyData]
  );

  const roleTitle = rows.authManager.role_name || (editId ? "Role" : "New Role");

  const hasPriv = (module: string, priv: string) =>
    toPrivilegeList(rows.authManager?.info?.[module]).includes(priv);

  const parentState = (parent: string, children: string[], priv: string) => {
    const keys = [parent, ...children];
    const checkedCount = keys.filter((k) => hasPriv(k, priv)).length;
    return {
      checked: checkedCount === keys.length && keys.length > 0,
      indeterminate: checkedCount > 0 && checkedCount < keys.length,
    };
  };

  const formBody = (
    <div
      className={`relative flex flex-col bg-white p-4 pb-0 sm:p-5 sm:pb-0 lg:p-6 lg:pb-0 ${
        isEmbedded
          ? "h-[min(92vh,900px)] w-full max-w-6xl overflow-hidden"
          : "min-h-[calc(100vh-68px)]"
      }`}
    >
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B] sm:text-[28px]">
            Roles & Privileges for {roleTitle}
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Manage and configure access permissions for this role.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <YhButton variant="secondary" size="sm" onClick={() => {}}>
            Duplicate Role
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            loading={saving}
            onClick={submitForm}
          >
            {editId ? "Save Changes" : "Save Role"}
          </YhButton>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto pb-24">
        <section className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] sm:p-6">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <label className="relative inline-flex h-6 w-11 cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={rows.authManager.isOrganization === 1}
                onChange={(e) => {
                  setRows({
                    authManager: {
                      ...emptyAuthManager(),
                      isOrganization: e.target.checked ? 1 : 0,
                    },
                  });
                }}
              />
              <span className="absolute inset-0 rounded-full bg-[#D1D5DB] transition peer-checked:bg-[#A02448]" />
              <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
            </label>
            <span className="text-sm font-semibold text-[#374151]">
              {rows.authManager.isOrganization === 1 ? "Organization" : "Team"}
            </span>
            <span className="inline-flex rounded-full bg-[#D1FAE5] px-2.5 py-0.5 text-xs font-semibold text-[#059669]">
              Active
            </span>
          </div>

          <div className="mb-6 grid gap-4 sm:grid-cols-2">
            <YhSelect
              label="Hierarchy"
              placeholder="Select Hierarchy"
              value={rows.authManager.hierarchyId || ""}
              options={hierarchyOptions}
              onChange={(e) => {
                const selected = hierarchyData.find(
                  (h: any) => String(h.id) === String(e.target.value)
                );
                setRows({
                  ...rows,
                  authManager: {
                    ...rows.authManager,
                    hierarchyId: selected?.id || e.target.value,
                    role_name: selected?.label || "",
                  },
                });
              }}
            />
            <YhInput
              label="Role Description"
              placeholder="An Hr Can view only Team & Salary related info"
              name="description"
              value={rows?.authManager?.description || ""}
              onChange={handleOnChangeInputField}
            />
          </div>

          <div className="overflow-x-auto rounded-xl border border-[#EEF0F5]">
            <table className="min-w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-[#EEF0F5] bg-[#FAFBFC]">
                  <th className="sticky left-0 z-10 bg-[#FAFBFC] px-4 py-3 text-xs font-semibold text-[#6B7280]">
                    Module Name
                  </th>
                  {PRIVILEGE_COLS.map((col) => (
                    <th
                      key={col.key}
                      className="px-2 py-3 text-center text-xs font-semibold text-[#6B7280]"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span
                          className={
                            col.key === "delete" ? "text-[#EF4444]" : "text-[#9CA3AF]"
                          }
                        >
                          <ColIcon name={col.icon} />
                        </span>
                        {col.label}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODULE_TREE.map(({ parent, children }) => {
                  const isOpen = expanded[parent];
                  return (
                    <React.Fragment key={parent}>
                      <tr className="border-b border-[#F3F4F6] bg-white">
                        <td className="sticky left-0 z-10 bg-white px-4 py-3">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 text-sm font-bold text-[#111827]"
                            onClick={() =>
                              setExpanded((p) => ({
                                ...p,
                                [parent]: !p[parent],
                              }))
                            }
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#9CA3AF"
                              strokeWidth="2"
                              className={`transition ${isOpen ? "rotate-90" : ""}`}
                            >
                              <path d="M9 18l6-6-6-6" />
                            </svg>
                            {moduleLabel(parent)}
                          </button>
                        </td>
                        {PRIVILEGE_COLS.map((col) => {
                          const state = parentState(parent, children, col.key);
                          return (
                            <td key={col.key} className="px-2 py-3 text-center">
                              <PrivilegeCheck
                                inputId={`priv-${safeDomId(parent)}-${safeDomId(col.key)}`}
                                checked={state.checked}
                                indeterminate={state.indeterminate}
                                onToggle={(checked) =>
                                  toggleParentPrivilege(
                                    parent,
                                    children,
                                    col.key,
                                    checked
                                  )
                                }
                              />
                            </td>
                          );
                        })}
                      </tr>
                      {isOpen
                        ? children.map((child) => (
                            <tr
                              key={child}
                              className="border-b border-[#F3F4F6] bg-white"
                            >
                              <td className="sticky left-0 z-10 bg-white px-4 py-2.5 pl-10 text-sm text-[#374151]">
                                {moduleLabel(child)}
                              </td>
                              {PRIVILEGE_COLS.map((col) => (
                                <td
                                  key={col.key}
                                  className="px-2 py-2.5 text-center"
                                >
                                  <PrivilegeCheck
                                    inputId={`priv-${safeDomId(child)}-${safeDomId(col.key)}`}
                                    checked={hasPriv(child, col.key)}
                                    onToggle={(checked) =>
                                      toggleModulePrivilege(
                                        child,
                                        col.key,
                                        checked
                                      )
                                    }
                                  />
                                </td>
                              ))}
                            </tr>
                          ))
                        : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="sticky bottom-0 z-20 -mx-4 mt-6 flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6">
        <button
          type="button"
          onClick={handleClose}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Back"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <YhButton variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            loading={saving}
            onClick={submitForm}
          >
            {editId ? "Save Changes" : "Save Role"}
          </YhButton>
        </div>
      </div>
    </div>
  );

  if (isEmbedded) {
    if (!props.isDialogOpen) return null;
    return (
      <RolePageErrorBoundary>
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-6xl overflow-hidden rounded-2xl shadow-xl">
            {formBody}
          </div>
        </div>
      </RolePageErrorBoundary>
    );
  }

  return <RolePageErrorBoundary>{formBody}</RolePageErrorBoundary>;
};

export default AddRolePage;
